import { KpiStatus } from './../enums/KPIStatus';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { KPI } from '../models/KPI';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { KPIService } from '../services/kpi.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { UserService } from '../services/user.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-kpis',
  standalone: true,
  imports: [
    MatTableModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatIconModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatMenuModule
  ],
  templateUrl: './kpis.component.html',
  styleUrl: './kpis.component.scss'
})
export class KpisComponent implements OnInit {
  public dataSource = new MatTableDataSource<KPI>([]);
  public kpisData: KPI[] = [];
  public selectedYear: string = '';
  public companies?: string[] = [];
  public kpiYears: number[] = [];
  public yearNumber!: number;
  public isLoading: boolean = false;
  public showLock: boolean = false;
  public editedObjects: KPI[] = [];
  public displayedColumns: string[] = [
    'KPI',
    'Tag',
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  public newValues: { [tag: string]: { [month: string]: number } } = {};
  public isEditing: boolean = false;
  public isEditingRowIndex: number | null = null;
  public editingColumnIndex: number | null = null;
  public currentUserDetails: any;
  public selectedCompany: string = '';
  public KpiStatus = KpiStatus;
  public isCurrentUserAdmin: boolean = false;
  public currentMonth: number = new Date().getMonth() + 1;

  constructor(private readonly cdRef: ChangeDetectorRef,
    private readonly kpiService: KPIService,
    public snackBar: MatSnackBar,
    private readonly authService: MsalService,
    private readonly userService: UserService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.currentUserDetails = this.authService.instance.getActiveAccount();
    this.userService.userRole$.subscribe(role => {
      if (role === 'Admin') {
        this.isCurrentUserAdmin = true;
      }
    });

    this.isLoading = true;
    this.getCompanies().pipe(
      switchMap((companies) => {
        if (!companies) {
          throw new Error('No companies available');
        }

        return this.getKPIYears(this.selectedCompany);
      }),
      switchMap(() => {
        return this.getKPIsData(this.selectedCompany, this.yearNumber);
      })
    ).subscribe({
      error: () => {
        this.snackBar.open('Error fetching KPIs data', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });;
      },
    });
  }

  getCompanies() {
    if (this.currentUserDetails?.idTokenClaims) {
      return this.userService.getUsersCompanies(this.currentUserDetails?.idTokenClaims?.oid as string).pipe(
        tap((data: string[]) => {
          this.companies = data;
          this.selectedCompany = data[0];
        }),
        catchError(() => {
          this.isLoading = false;
          this.snackBar.open('Error fetching companies', 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          return of(null);
        })
      );
    } else {
      return of(null);
    }
  }

  fetchYearsAndKPIsForCompany(company: string): void {
    this.getKPIYears(company).pipe(
      switchMap(() => {
        return this.getKPIsData(company, this.yearNumber);
      })
    ).subscribe({
      error: (err) => {
        this.isLoading = false;
        this.snackBar.open('Error fetching KPIs data', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });;
      }
    });
  }

  onCompanyChange(company: string) {
    this.isLoading = true;
    this.fetchYearsAndKPIsForCompany(company);
  }

  onYearChange(): void {
    this.isLoading = true;
    this.selectedYear = this.yearNumber.toString();
    this.getKPIsData(this.selectedCompany, this.yearNumber).subscribe({
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Error fetching KPIs data', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      }
    });
  }

  public getMonthValue(kpi: KPI, month: number): number | null {
    const targetMonth = month - 1; // Convert to 0-based index for JavaScript months

    const dateValue = kpi.dateValues.find(dv => {
      const dateObj = new Date(dv.date);
      return dateObj.getMonth() === targetMonth;
    });

    return dateValue ? dateValue.value : 0;
  }

  getTooltipText(element: KPI, monthIndex: number): string {
    const dateValue = element.dateValues.find(
      dv => new Date(dv.date).getMonth() + 1 === monthIndex
    );

    if (dateValue) {
      if (dateValue.modified) {
        const modifiedDate = new Date(dateValue.modified).toLocaleDateString();
        return `Modifier: ${dateValue.modifier} <br>
        Modified on: ${modifiedDate} <br>`;
      }
    }

    return 'Not modified';
  }

  public isPastMonth(month: number): boolean {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // Current month (0-based index)
    const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();

    // If the selected year is in the past, all months are "past"
    if (this.yearNumber < currentYear) {
      return true;
    }

    // If the selected year is the current year, apply normal logic
    const targetDate = new Date(this.yearNumber, month - 1); // Convert input month to Date
    const oneMonthBeforeCurrent = new Date(currentYear, currentMonth - 1); // Month before current

    // If the current day is > 15, consider the previous month as "past"
    const includePreviousMonth = currentDay > 15;

    if (includePreviousMonth) {
      return targetDate <= oneMonthBeforeCurrent;
    } else {
      return targetDate < oneMonthBeforeCurrent; // Strictly exclude the previous month if day <= 15
    }
  }

  getLockedInfo(element: KPI, monthIndex: number) {
    let editingDateValue = element.dateValues.find(
      dv => new Date(dv.date).getMonth() + 1 === monthIndex
    );

    this.showLock = !!editingDateValue ? editingDateValue.locked : false;
    return editingDateValue?.locked;
  }

  getClass(element: KPI, monthIndex: number): string {
    // Find the dateValue corresponding to the given monthIndex
    const dateValue = element.dateValues.find(dv => {
      const date = new Date(dv.date);
      return date.getMonth() + 1 === monthIndex; // Months are 0-based in JavaScript
    });

    if (!dateValue) {
      return ''; // No matching dateValue for the given monthIndex
    }

    const isPast = this.isPastMonth(monthIndex);
    const isLocked = this.getLockedInfo(element, monthIndex);
    if (element.tag === 'KPI.FTE' || element.tag === 'KPI.HC' || element.tag === 'KPI.TOTHOU'
      || element.tag === 'KPI.TOTAVHOU' || element.tag === 'KPI.TOTAVHOU.PROD' || element.tag === 'KPI.UTIL.PROD'
      || element.tag === 'KPI.AVAI' || element.tag === 'KPI.EXT.PROD') {
      return 'past-date-and-locked-cell';
    }
    // Determine class based on conditions
    if (isPast && isLocked) {
      return 'past-date-and-locked-cell';
    }

    if (dateValue.status === KpiStatus.Empty || dateValue.status === KpiStatus.AwaitingApproval) {
      return 'no-data-and-submitted-cell';
    }

    if (dateValue.status === KpiStatus.OK && !isLocked) {
      return 'approved-and-unlocked-cell';
    }

    return '';
  }

  lockUnlockElement(element: KPI, lock: boolean, monthIndex: number) {
    this.isLoading = true;
    let existingDateValue = element.dateValues.find(
      dv => new Date(dv.date).getMonth() + 1 === monthIndex
    );

    let dateConst = `${this.yearNumber}-${monthIndex}-01`;

    if (monthIndex < 10) {
      dateConst = `${this.yearNumber}-0${monthIndex}-01`
    }

    if (existingDateValue == undefined) {
      existingDateValue = {
        date: dateConst,
        value: null,
        modified: null,
        modifier: '',
        locked: false,
        status: null
      }
    }

    const updatedObject: KPI = {
      name: element.name,
      tag: element.tag,
      company: element.company,
      dateValues: [
        {
          date: existingDateValue.date,
          value: existingDateValue.value,
          locked: lock,
          modified: existingDateValue.modified,
          modifier: existingDateValue.modifier,
          status: existingDateValue.status
        },
      ]
    }

    let updatedObjects = [];
    updatedObjects.push(updatedObject);

    this.updateKPI(updatedObjects, false, true);
  }

  updateKPI(updatedObjects: KPI[], isEdit: boolean, isLockChanged: boolean) {
    this.kpiService.updateItem(updatedObjects, isEdit, isLockChanged).subscribe({
      next: () => {
        this.getKPIsData(this.selectedCompany, this.yearNumber).subscribe({
          next: () => {
            this.snackBar.open('Update successful', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          },
          error: () => {
            this.snackBar.open('Error fetching KPIs data after update', 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          }
        });
      },
      error: () => {
        // Handle the error case for update
        this.snackBar.open('Update failed. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  isPastMonthForMenu(month: number): boolean {
    if (this.yearNumber < new Date().getFullYear()) {
      return true;
    }
    if (this.yearNumber > new Date().getFullYear()) {
      return false;
    }
    return month <= this.currentMonth;
  }

  onCellInput(event: Event, element: KPI, monthIndex: number): void {
    const target = event.target as HTMLElement;
    const newValue = target.textContent || '';
    const numericValue = parseFloat(newValue) || 0;
    let existingDateValue = element.dateValues.find(
      dv => new Date(dv.date).getMonth() + 1 === monthIndex
    );

    let dateConst = `${this.yearNumber}-${monthIndex}-01`;

    if (monthIndex < 10) {
      dateConst = `${this.yearNumber}-0${monthIndex}-01`
    }

    if (existingDateValue == undefined) {
      existingDateValue = {
        date: dateConst,
        value: 0,
        modified: new Date(),
        modifier: '',
        locked: false,
        status: null
      }
    }

    const existingObjectIndex = this.editedObjects.findIndex(
      (obj) =>
        obj.name === element.name &&
        obj.tag === element.tag &&
        obj.dateValues.some(
          (objDateValue) =>
            element.dateValues.some(
              (elementDateValue) => objDateValue.date === existingDateValue?.date
            )
        )
    );

    if (existingObjectIndex !== -1) {
      this.editedObjects[existingObjectIndex].dateValues[0] = {
        date: existingDateValue.date,
        value: numericValue,
        locked: false,
        modified: new Date(),
        modifier: this.currentUserDetails.name,
        status: existingDateValue.status
      };
    } else {
      const updatedObject: KPI = {
        name: element.name,
        tag: element.tag,
        company: element.company,
        dateValues: [
          {
            date: existingDateValue.date,
            value: numericValue,
            locked: false,
            modified: new Date(),
            modifier: this.currentUserDetails.name,
            status: KpiStatus.AwaitingApproval
          },
        ],
      };

      this.editedObjects.push(updatedObject);
    }

    this.cdRef.detectChanges();
  }

  startEditingForColumn(monthIndex: number): void {
    this.editingColumnIndex = monthIndex;
    this.isEditing = true;
  }

  saveColumnChanges(monthIndex: number): void {
    this.editingColumnIndex = null;
    this.isEditing = false;
    this.isEditingRowIndex = null;
    this.isLoading = true;
    this.updateKPI(this.editedObjects, true, false);
  }

  approveKPIs(monthIndex: number): void {
    let targetDate = `${this.selectedYear}-${monthIndex.toString().padStart(2, '0')}-01`;
    const result = this.kpisData
      .filter(item =>
        item.dateValues.some(dateValue => dateValue.date === targetDate)
      )
      .map(item => {
        const matchingDateValues = item.dateValues
          .filter(dateValue => dateValue.date === targetDate)
          .map(dateValue => ({
            ...dateValue,
            status: KpiStatus.OK // Set status at the dateValue level
          }));

        return {
          ...item,
          dateValues: matchingDateValues
        };
      });

    this.editingColumnIndex = null;
    this.isEditing = false;
    this.isEditingRowIndex = null;
    this.isLoading = true;
    this.updateKPI(result, false, false);
  }

  cancelColumnEditing(): void {
    this.editingColumnIndex = null;
    this.isEditing = false;
    this.isEditingRowIndex = null;
  }

  getKPIYears(selectedCompany: string): Observable<number[]> {
    return this.kpiService.getKpiYears(selectedCompany).pipe(
      tap((years) => {
        this.kpiYears = years;
        const currentYear = new Date().getFullYear();
        if (this.kpiYears.length === 0) {
          this.kpiYears.push(currentYear);
        }
        this.selectedYear = this.kpiYears.includes(currentYear)
          ? currentYear.toString()
          : this.kpiYears[0].toString();
        this.yearNumber = this.kpiYears.includes(currentYear) ? currentYear : this.kpiYears[0];
        this.cdr.detectChanges();
      }),
      catchError((err) => {
        this.isLoading = false;
        this.snackBar.open('Error fetching KPI years', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
        return of([]);
      })
    );
  }

  getKPIsData(selectedCompany: string, year: number): Observable<any> {
    if (selectedCompany.includes('acobia')) { // this code is temporary (added by request from client), until they sync the companies list
      selectedCompany = 'acobia';
    }
    return this.kpiService.getKPIsData(selectedCompany, year).pipe(
      tap((data) => {
        this.kpisData = data;
        this.kpisData.forEach(x => {
          if (x.tag === 'KPI.FTE' || x.tag === 'KPI.HC' || x.tag === 'KPI.TOTHOU' || x.tag === 'KPI.TOTAVHOU' || x.tag === 'KPI.TOTAVHOU.PROD' || x.tag === 'KPI.UTIL.PROD' || x.tag === 'KPI.AVAI' || x.tag === 'KPI.EXT.PROD') {
            x.isFormula = true;
          } else {
            x.isFormula = false;
          }
        });

        this.isLoading = false;
      }),
      catchError((err) => {
        this.isLoading = false;
        this.snackBar.open('Error fetching KPIs data', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
        return of(null);
      })
    );
  }

  editCell(rowIndex: any): void {
    if (this.isEditing && rowIndex == this.isEditingRowIndex) {
      if (this.isEditingRowIndex === rowIndex) {
        return;
      }

      this.isEditingRowIndex = rowIndex;
    }
  }
}
